import { request } from "hooks/useOkoApi/useOkoApi";

const BASE = "admin/lesson-report-console";
const POLL_MS = 2500;
// Results live in Redis for 1h; stop well before that.
const POLL_TIMEOUT_MS = 45 * 60 * 1000;

// POSTs never retry: a retried 5xx would enqueue a second paid model call.
const post = (path, payload) =>
  request(`${BASE}/${path}`, { method: "POST", payload, maxRetries: 0 }).then((r) => r.job_ids);

export const getDefaults = () => request(`${BASE}/defaults`, { method: "GET" });

export const getLessonGroups = (educatorId) =>
  request(`${BASE}/educators/${educatorId}/lesson-groups`, { method: "GET" })
    .then((r) => r.lesson_groups);

export const getClassrooms = (educatorId) =>
  request(`${BASE}/educators/${educatorId}/classrooms`, { method: "GET" })
    .then((r) => r.classrooms || []);

// `scope` is { assignment_id } or { classroom_id, start_date, end_date } (end exclusive).
export const submitPacket = (educatorId, scope) =>
  post("packets/jobs", {
    educator_id: educatorId,
    report_type: scope.classroom_id ? "date" : "lesson_group",
    ...scope,
  }).then((ids) => ids[0]);

// One POST per variant: jobs from a single POST run one after another.
export const submitRun = (packet, variant) =>
  post("runs/jobs", { packet, variants: [variant] }).then((ids) => ids[0]);

// Date packets send the experiment's own start/end dates: the packet's period
// is a datetime that may land on another day in this browser's timezone.
export const getLabels = (packet, experiment) => {
  const params = new URLSearchParams({ educator_id: packet.educator_id });
  const byDate = packet.report_type === "date";
  if (byDate) {
    if (!experiment?.start_date || !experiment?.end_date) {
      return Promise.reject(new Error("This experiment has no saved date range, so names can't be looked up."));
    }
    params.append("start_date", experiment.start_date);
    params.append("end_date", experiment.end_date);
  }
  packet.session_ids.forEach((id) => params.append("session_id", id));
  // Labels only students who had joined when the packet was built.
  params.append("built_at", packet.built_at);
  const scope = byDate ? `classrooms/${packet.classroom_id}` : `assignments/${packet.assignment_id}`;
  return request(`${BASE}/${scope}/labels?${params}`, { method: "GET" }).then((r) => r.labels);
};

// { status: "pending" | "ready" | "unavailable", cost_usd, input_tokens, ... }.
// No retries here: the caller polls, and Langfuse rate-limits trace reads.
export const getRunMetrics = (traceId) =>
  request(`${BASE}/traces/${encodeURIComponent(traceId)}/metrics`, { method: "GET", maxRetries: 0 });

const asText = (value) => (typeof value === "string" ? value : JSON.stringify(value));

// Always a string: an object `detail` rendered as a React child would unmount the console.
export const errorMessage = (e) => {
  if (e?.status === 403) return "Access denied: superuser or staff only.";
  if (Array.isArray(e?.detail)) return e.detail.map((d) => d?.msg || asText(d)).join("; ");
  if (e?.detail) return asText(e.detail);
  if (e?.status === 404) return "Not found or expired.";
  return e?.message || "Request failed.";
};

// A server-reported failure (and a 404) is final; other poll errors can be resumed.
export class JobFailed extends Error {}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Resolves with the job's `result`. Rejects with JobFailed carrying the server's
// `error` verbatim, with the request error (404 = expired), or with a plain
// Error when cancelled or after POLL_TIMEOUT_MS.
export const pollJob = async (jobId, isCancelled = () => false) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    if (isCancelled()) throw new Error("cancelled");
    const body = await request(`${BASE}/jobs/${jobId}`, { method: "GET" });
    if (body.status === "completed") return body.result;
    if (body.status === "failed") throw new JobFailed(body.error ? asText(body.error) : "Job failed.");
    await sleep(POLL_MS);
  }
  throw new Error("Timed out waiting for the job.");
};
